import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { WhatsappConnectionState, WhatsappConnectionResponse } from '../types';
import { useSyncIntegration, useRemoveIntegration } from '../../../../hooks';
import { PROVIDER_TYPE_MAP, Integration } from '@utils/constants';

const INITIAL_STATE: WhatsappConnectionState = {
  isQrVisible: false,
  qrCode: null,
  connectionStatus: 'DISCONNECTED',
  isConnecting: false,
  isVerifying: false,
  instanceName: null,
  error: null,
};

interface UseWhatsappConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

export const useWhatsappConnection = (integration?: UseWhatsappConnectionProps): ConnectionHookResult => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();

  const [state, setState] = useState<WhatsappConnectionState>(() => {
    // Si tenemos integration.id, asumimos que está conectado o en proceso (dependiendo de la data real)
    if (integration?.id) {
      return {
        ...INITIAL_STATE,
        connectionStatus: 'CONNECTED',
        // Si tenemos la config en el prop, podríamos sacar el instanceName
        instanceName: (integration.configuration?.instance_name as string) || null,
      };
    }
    return {
      ...INITIAL_STATE,
    };
  });

  const handleConnectionResponse = useCallback((response: WhatsappConnectionResponse) => {
    if (response.status === 'QR_CODE' && response.qrcode) {
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        isVerifying: false,
        isQrVisible: true,
        qrCode: response.qrcode!,
        connectionStatus: 'QR_CODE',
        instanceName: response.instanceName || null,
        error: null,
      }));
    } else if (response.status === 'CONNECTED') {
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        isVerifying: false,
        isQrVisible: false,
        qrCode: null,
        connectionStatus: 'CONNECTED',
        instanceName: response.instanceName || null,
        error: null,
      }));
      toast.success('¡WhatsApp conectado!');
    } else if (response.status === 'DISCONNECTED') {
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        isVerifying: false,
        connectionStatus: 'DISCONNECTED',
        error: response.message || 'Desconectado',
      }));
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await syncIntegration<WhatsappConnectionResponse>(integration as unknown as Integration, {
        action: 'SHOW_STATUS',
        notify: false,
      });
      if (response) {
        handleConnectionResponse(response);
        return response;
      }
    } catch (error) {
      return null;
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  const connect = useCallback(async () => {
    if (!integration) {
      toast.error('Error de configuración: No se encontró la integración');
      return;
    }

    setState((prev) => ({ ...prev, isConnecting: true, error: null }));

    try {
      // payload para connect
      const response = await syncIntegration<WhatsappConnectionResponse>(
        integration as unknown as Integration, // Cast necesario por diferencia de tipos estricta
        { action: 'CONNECT', notify: false },
      );

      if (response) {
        handleConnectionResponse(response);
      }
    } catch (error) {
      const err = error as { response?: { status: number }; message?: string };

      // Si el error es 403 (Instancia ya existe) o mensaje relacionado, intentamos obtener el status
      // El backend puede enviar el error de diferentes formas, intentamos capturar varias
      if (
        err?.response?.status === 403 ||
        err?.message?.includes('already in use') ||
        err?.message?.includes('403') ||
        JSON.stringify(err).includes('already in use')
      ) {
        const statusResponse = await fetchStatus();
        if (statusResponse?.status === 'CONNECTED') {
          return; // Ya se manejó en fetchStatus
        }
      }

      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: 'Error al conectar. Intenta nuevamente.',
      }));
    }
  }, [integration, syncIntegration, handleConnectionResponse, fetchStatus]);

  /* Helper para delay */
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const verifyConnection = useCallback(async () => {
    if (!integration) return;

    setState((prev) => ({ ...prev, isVerifying: true, error: null }));

    let attempts = 0;
    const maxAttempts = 10; // Aumentamos intentos
    const pollInterval = 3000; // 3 segundos entre intentos

    try {
      while (attempts < maxAttempts) {
        // Usamos SHOW_STATUS para verificar
        const response = await syncIntegration<WhatsappConnectionResponse>(integration as unknown as Integration, {
          action: 'SHOW_STATUS',
          notify: false,
        });

        if (response) {
          const isConnected = response.status === 'CONNECTED';
          const isPending =
            response.config?.connection_status === 'PENDING' || response.config?.connection_status === 'open';

          if (isConnected || isPending) {
            handleConnectionResponse({
              ...response,
              status: 'CONNECTED', // Forzamos estado conectado visualmente si está pendiente/open
            });
            return; // Éxito, salimos del polling
          }
          // Si devuelve QR_CODE u otro estado, seguimos intentando
        } else {
          // Si la respuesta es vacía (null/undefined/{}), seguimos intentando
          // Esto maneja el caso donde el backend retorna 204 o json vacío temporalmente
        }

        // Esperamos antes del siguiente intento
        await delay(pollInterval);
        attempts++;
      }

      // Si llegamos aquí, no se conectó tras los intentos
      toast.error('No se detectó la conexión. Asegúrate de haber escaneado el código.');
      setState((prev) => ({ ...prev, isVerifying: false })); // Dejamos de verificar pero mantenemos modal
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isVerifying: false,
        error: 'No se pudo verificar la conexión.',
      }));
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  const disconnect = useCallback(async () => {
    if (!integration) return;

    setState((prev) => ({ ...prev, isConnecting: true }));

    try {
      // Usamos removeIntegration del hook generico
      await removeIntegration(integration as unknown as Integration);

      setState(INITIAL_STATE);
      // Toast ya lo maneja removeIntegration
    } catch (error) {
      setState((prev) => ({ ...prev, isConnecting: false }));
    }
  }, [integration, removeIntegration]);

  const closeQrModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isQrVisible: false,
      qrCode: null,
      // Si cancelamos QR, volvemos a estado anterior o desconectado
      connectionStatus: prev.connectionStatus === 'QR_CODE' ? 'DISCONNECTED' : prev.connectionStatus,
    }));
  }, []);

  // Sync initial state with integration prop presence
  // Si integration existe (id), asumimos que está conectado visualmente en Card,
  // pero el hook maneja el estado interno.
  // Podríamos intentar verificar estado al montar si existe integration?
  // Por ahora dejamos que el usuario inicie acciones.

  return {
    isConnected:
      state.connectionStatus === 'CONNECTED' ||
      (Boolean(integration?.id) && state.connectionStatus !== 'DISCONNECTED' && state.connectionStatus !== 'QR_CODE'),
    isConnecting: state.isConnecting,
    connect,
    disconnect,
    status: state.connectionStatus,
    instanceName: state.instanceName,
    isQrVisible: state.isQrVisible,
    qrCode: state.qrCode,
    onVerify: verifyConnection,
    onCloseQr: closeQrModal,
  };
};
